<template>
  <div class="max-w-lg mx-auto mt-12">
    <h1 class="text-3xl font-bold mb-2">{{ $t('invites.title') }}</h1>
    <p class="text-gray-400 mb-8">{{ $t('invites.subtitle') }}</p>

    <div v-if="loading" class="text-gray-400">{{ $t('common.loading') }}</div>

    <div v-else-if="store.invites.length === 0" class="text-center py-12 bg-gray-800 rounded-xl">
      <span class="material-icons text-5xl text-gray-500 mb-3">mail_outline</span>
      <p class="text-gray-400">{{ $t('invites.noInvites') }}</p>
      <router-link
        v-if="defaultWorkspaceId"
        :to="{ name: 'Bots', params: { workspaceId: defaultWorkspaceId } }"
        class="mt-4 inline-block px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg text-sm"
      >
        {{ $t('invites.goToBots') }}
      </router-link>
    </div>

    <ul v-else class="space-y-4">
      <li
        v-for="invite in store.invites"
        :key="invite._id"
        class="bg-gray-800 rounded-xl p-5 border border-gray-700"
      >
        <p class="font-semibold text-white text-lg">{{ invite.workspaceId?.name }}</p>
        <p class="text-gray-400 text-sm mt-1">
          {{ $t('invites.invitedBy') }} <span class="text-gray-300">{{ invite.invitedBy?.name }}</span>
        </p>
        <div class="flex gap-3 mt-4">
          <!-- Both Buttons of the invite are disabled while it is busy; only the
               clicked one shows the spinner. -->
          <Button
            severity="primary"
            :label="$t('invites.accept')"
            :loading="busyInviteId === invite._id && busyAction === 'accept'"
            :disabled="busyInviteId === invite._id"
            @click="accept(invite._id)"
          />
          <Button
            severity="secondary"
            :label="$t('invites.decline')"
            :loading="busyInviteId === invite._id && busyAction === 'decline'"
            :disabled="busyInviteId === invite._id"
            @click="decline(invite._id)"
          />
        </div>
      </li>
    </ul>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useWorkspaceStore } from '../stores/workspaceStore';

const router = useRouter();
const store = useWorkspaceStore();

const loading = ref(false);
const defaultWorkspaceId = ref(null);
const busyInviteId = ref(null);
/** Action running on busyInviteId ('accept' | 'decline'): the spinner goes on that Button only. */
const busyAction = ref(null);

async function load() {
  loading.value = true;
  try {
    await store.loadInvites();
  } finally {
    loading.value = false;
  }

  // Resolve default workspace for the "go to bots" link
  if (!store.workspaces.length) await store.loadWorkspaces();
  const ws = store.resolveWorkspace(store.getLastWorkspaceId());
  if (ws) defaultWorkspaceId.value = ws._id;
}

async function accept(inviteId) {
  busyInviteId.value = inviteId;
  busyAction.value = 'accept';
  try {
    const res = await store.acceptInvite(inviteId);
    if (res?.success) {
      router.push({ name: 'Bots', params: { workspaceId: res.data.workspaceId } });
    }
  } finally {
    busyInviteId.value = null;
    busyAction.value = null;
  }
}

async function decline(inviteId) {
  busyInviteId.value = inviteId;
  busyAction.value = 'decline';
  try {
    await store.declineInvite(inviteId);
  } finally {
    busyInviteId.value = null;
    busyAction.value = null;
  }
}

onMounted(load);
</script>
